import React, { useState, useEffect } from 'react';
import { Product, Category, FALLBACK_PRODUCTS } from '../types';
import { Button } from '../components/ui/Button';
import { supabase } from '../services/supabaseClient';
import { Helmet } from 'react-helmet-async';
import { ShoppingBag, Star, Clock, Leaf, Filter, X, ChevronRight, ChevronLeft, Eye, Truck, Package, Maximize2 } from 'lucide-react';

interface MerchProps {
  addToCart: (product: Product) => void;
  siteConfig?: any;
  defaultType?: 'all' | 'tejidos' | 'shopping' | 'tunantada' | 'eventos';
  lockedType?: 'tejidos' | 'shopping' | 'tunantada' | 'eventos';
  heroTitle?: string;
  heroSubtitle?: string;
  heroImage?: string;
  hideHero?: boolean;
  isStorefront?: boolean;
}

export const Merch: React.FC<MerchProps> = ({
  addToCart,
  siteConfig,
  defaultType = 'all',
  lockedType,
  heroTitle,
  heroSubtitle,
  heroImage,
  hideHero = false,
  isStorefront = false
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategoryId, setActiveCategoryId] = useState<number | 'all'>('all');
  const [activeType, setActiveType] = useState<'all' | 'tejidos' | 'shopping' | 'tunantada' | 'eventos'>(lockedType || defaultType);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [categoryFilterOpen, setCategoryFilterOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [containerIndex, setContainerIndex] = useState(0);

  const handleOpenLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  useEffect(() => {
    if (!lockedType) {
      setActiveType(defaultType);
    } else {
      setActiveType(lockedType);
    }
    setActiveCategoryId('all');
  }, [defaultType, lockedType]);

  // DEFAULTS
  const defaults = {
    heroTitle: heroTitle || siteConfig?.merch_hero_title || 'Colección Mabelita',
    heroSubtitle: heroSubtitle || siteConfig?.merch_hero_subtitle || 'Descubre todo nuestro catálogo en una experiencia premium.',
    heroImage: heroImage || siteConfig?.merch_hero_image || 'https://images.unsplash.com/photo-1516280440614-6697288d5d38?auto=format&fit=crop&w=1920&q=80',
  };

  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        setLoading(true);

        // Fetch ALL Categories
        const { data: catsData } = await supabase.from('categorias').select('*');
        let fetchedCategories: Category[] = [];
        if (catsData) {
          fetchedCategories = catsData.map((c: any) => ({
            id: c.id,
            name: c.nombre,
            slug: c.slug,
            type: c.tipo
          }));
          setCategories(fetchedCategories);
        }

        // Fetch ALL Products
        const { data, error } = await supabase.from('productos').select('*');

        if (error || !data || data.length === 0) {
          setProducts([]);
        } else {
          const mappedProducts: Product[] = data.map((p: any) => ({
            id: p.id,
            name: p.nombre,
            slug: p.slug,
            description: p.descripcion,
            descripcion_html: p.descripcion_html,
            video_url: p.video_url,
            price: p.precio ? Number(p.precio) : 0,
            category_id: p.categoria_id,
            image_url: p.imagen_url || p.imagen_principal || 'https://via.placeholder.com/400',
            days_to_make: p.dias_elaboracion || 0,
            stock: p.stock || 0,
            is_featured: p.destacado || false,
            imagenes_adicionales: p.imagenes_adicionales || [],
            stripe_price_id: p.stripe_price_id,
            shipping_cost_national: p.shipping_cost_national || 0,
            shipping_cost_international: p.shipping_cost_international || 0
          }));
          setProducts(mappedProducts);
        }
      } catch (e) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStoreData();
  }, []);

  const handleAddToCart = (product: Product, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    addToCart(product);
    setSelectedProduct(null);
  };

  // Filtrado compuesto
  const filteredProducts = products.filter(p => {
    const cat = categories.find(c => c.id === p.category_id);

    // Si ESTAMOS en el storefront de Tejiendo, SOLO mostramos tejidos
    if (isStorefront && cat?.type !== 'tejidos') {
      return false;
    }

    // Si NO estamos en el storefront de Tejiendo, ocultamos los productos de artesanía (tejidos)
    if (!isStorefront && cat?.type === 'tejidos') {
      return false;
    }

    const catTypeMatch = activeType === 'all' || cat?.type === activeType;
    const catIdMatch = activeCategoryId === 'all' || p.category_id === activeCategoryId;
    return catTypeMatch && catIdMatch;
  });

  return (
    <div className={`w-full bg-mabelita-surface text-mabelita-dark flex flex-col font-sans ${hideHero ? '' : 'pt-16 min-h-screen'}`}>
      <Helmet>
        <title>{`${activeType === 'tejidos' ? 'Tejidos Artesanales de Alpaca' : activeType === 'tunantada' ? 'Colección Tunantada' : 'Tienda Oficial'} | Mabelita`}</title>
        <meta name="description" content="Explora el catálogo oficial de Mabelita de la Cruz. Tejidos artesanales, prendas andinas y colecciones exclusivas de folklore peruano." />
      </Helmet>
      {/* Header PRO */}
      {!hideHero && (
        <div className="relative h-[28vh] md:h-[30vh] bg-mabelita-dark overflow-hidden flex items-end">
          <div className="absolute inset-0 bg-gradient-to-b from-mabelita-dark/60 via-mabelita-dark/40 to-mabelita-dark/90 z-10"></div>
          <img
            src={defaults.heroImage}
            alt="Colección"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
          <div className="relative z-20 px-8 pb-8 w-full max-w-7xl mx-auto">
            <div className="flex items-end gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-mabelita-accent font-bold mb-2">Catálogo Oficial</p>
                <h1 className="text-2xl md:text-3xl font-serif font-bold text-mabelita-light tracking-tight">
                  {defaults.heroTitle}
                </h1>
              </div>
              <div className="flex-1 h-px bg-white/20 mb-2 hidden md:block"></div>
              <p className="text-sm text-mabelita-light/70 font-light hidden md:block mb-1 max-w-xs text-right">
                {defaults.heroSubtitle}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={`flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-8 ${hideHero ? 'py-12' : 'py-8 md:py-16'}`}>

        {/* SIDEBAR FILTERS (Desktop) & MOBILE TOGGLE */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setCategoryFilterOpen(!categoryFilterOpen)}
              className="w-full flex items-center justify-between p-4 bg-mabelita-light rounded-xl shadow-sm font-bold text-mabelita-dark"
            >
              <span className="flex items-center gap-2"><Filter size={20} /> Filtros y Categorías</span>
              {categoryFilterOpen ? <X size={20} /> : <ChevronRight size={20} />}
            </button>
          </div>

          <div className={`lg:block ${categoryFilterOpen ? 'block' : 'hidden'} space-y-6 bg-mabelita-light p-6 rounded-2xl shadow-sm border border-mabelita-surface lg:sticky lg:top-24`}>

            {/* "Todas" button */}
            <button
              onClick={() => { setActiveType('all'); setActiveCategoryId('all'); }}
              className={`flex items-center justify-between w-full text-left px-4 py-3 rounded-xl text-sm transition-all ${activeType === 'all' && activeCategoryId === 'all' ? 'font-bold text-mabelita-primary border-2 border-mabelita-primary bg-mabelita-surface' : 'text-mabelita-dark/70 hover:bg-mabelita-surface hover:text-mabelita-dark font-medium border-2 border-transparent'}`}
            >
              <span>Todas</span>
              <span className="text-xs opacity-50">{categories.filter(c => isStorefront ? c.type === 'tejidos' : c.type !== 'tejidos').length}</span>
            </button>

            {/* GROUPED CATEGORY SECTIONS */}
            {(isStorefront
              ? [
                { type: 'tejidos' as const, label: 'Tejidos Artesanales', color: 'text-orange-600 bg-orange-50 border-orange-200' },
              ]
              : [
                { type: 'shopping' as const, label: 'Shopping con Mabelita', color: 'text-purple-600 bg-purple-50 border-purple-200' },
                { type: 'tunantada' as const, label: 'Tunantada', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
                { type: 'eventos' as const, label: 'Contratación Eventos', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
              ]
            ).map((section) => {
              const sectionCats = categories.filter(c => c.type === section.type);
              if (sectionCats.length === 0) return null;

              const isSectionActive = activeType === section.type && activeCategoryId === 'all';

              return (
                <div key={section.label} className="space-y-1">
                  {/* Section Header */}
                  <button
                    onClick={() => {
                      setActiveType(section.type as any);
                      setActiveCategoryId('all');
                    }}
                    className={`flex items-center gap-2 w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-all border ${isSectionActive ? section.color + ' border-current/20' : 'text-mabelita-dark/80 hover:bg-mabelita-surface border-transparent'}`}
                  >
                    <span className="flex-1">{section.label}</span>
                    <span className="text-[10px] font-bold opacity-50 tabular-nums">{sectionCats.length}</span>
                  </button>

                  {/* Subcategories */}
                  {(activeType === section.type || activeType === 'all') && (
                    <div className="ml-4 pl-3 border-l-2 border-mabelita-surface space-y-0.5">
                      {sectionCats.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => { setActiveType(section.type as any); setActiveCategoryId(cat.id); }}
                          className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${activeCategoryId === cat.id ? 'font-bold text-mabelita-primary bg-mabelita-surface' : 'text-mabelita-dark/60 hover:bg-mabelita-surface/50 hover:text-mabelita-dark'}`}
                        >
                          <span>{cat.name}</span>
                          <ChevronRight size={12} className={activeCategoryId === cat.id ? 'opacity-100 text-mabelita-primary' : 'opacity-0'} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* PRODUCTS GRID */}
        <div className="flex-1">
          <div className="mb-6 flex items-center justify-between">
            <p className="opacity-70 text-sm">Mostrando <span className="font-bold text-mabelita-primary">{filteredProducts.length}</span> productos</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 animate-pulse">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="aspect-[3/4] md:aspect-[4/5] bg-mabelita-surface rounded-2xl"></div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-mabelita-light rounded-3xl p-12 text-center shadow-sm border border-mabelita-surface">
              <Filter className="mx-auto h-12 w-12 text-mabelita-primary opacity-50 mb-4" />
              <h3 className="text-lg font-bold text-mabelita-dark mb-2">No hay productos disponibles</h3>
              <p className="opacity-70">Intenta cambiar los filtros o seleccionar otra categoría.</p>
              <Button onClick={() => { setActiveType('all'); setActiveCategoryId('all'); }} className="mt-6 bg-mabelita-primary text-mabelita-light hover:bg-mabelita-secondary">Limpiar Filtros</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {filteredProducts.map(product => {
                const isTejido = categories.find(c => c.id === product.category_id)?.type === 'tejidos';
                const isEvent = categories.find(c => c.id === product.category_id)?.type === 'tunantada' || categories.find(c => c.id === product.category_id)?.type === 'eventos';

                return (
                  <div
                    key={product.id}
                    onClick={() => { setSelectedProduct(product); setContainerIndex(0); }}
                    className="group flex flex-col cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-2xl md:rounded-3xl bg-mabelita-surface mb-4 md:mb-6 group-hover:shadow-2xl transition-all duration-500 border border-mabelita-surface/50">
                      {product.video_url ? (
                        <iframe
                          src={product.video_url}
                          className="w-full h-full border-none bg-black"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                          title={product.name}
                        />
                      ) : (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
                          loading="lazy"
                        />
                      )}

                      <div className="absolute top-2 left-2 md:top-4 md:left-4 flex flex-col gap-1 md:gap-2 z-10">
                        {isTejido && (
                          <span className="bg-mabelita-light/95 backdrop-blur-md text-[8px] md:text-[11px] font-bold uppercase tracking-widest px-2 py-1 md:px-3 md:py-1.5 rounded-full shadow-sm text-mabelita-accent">Artesanal</span>
                        )}
                        {product.is_featured && (
                          <span className="bg-mabelita-dark/90 backdrop-blur-md text-[8px] md:text-[11px] font-bold uppercase tracking-widest px-2 py-1 md:px-3 md:py-1.5 rounded-full shadow-sm text-mabelita-light">Top Ventas</span>
                        )}
                      </div>

                      {/* Hover Overlay Desktop */}
                      <div className="absolute inset-0 bg-mabelita-dark/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:flex flex-col justify-end p-6 z-20">
                        <button
                          onClick={(e) => {
                            const isEvent = categories.find(c => c.id === product.category_id)?.type === 'tunantada' || categories.find(c => c.id === product.category_id)?.type === 'eventos';
                            if (isEvent) {
                              e.stopPropagation();
                              window.location.hash = '#singer';
                            } else {
                              handleAddToCart(product, e);
                            }
                          }}
                          disabled={product.stock === 0 && product.days_to_make === 0}
                          className={`w-full font-bold py-4 rounded-xl shadow-2xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-widest text-sm ${product.stock === 0 && product.days_to_make === 0 ? 'bg-mabelita-surface text-mabelita-dark/50 cursor-not-allowed' : 'bg-mabelita-light text-mabelita-dark hover:bg-mabelita-primary hover:text-mabelita-light'}`}
                        >
                          {(categories.find(c => c.id === product.category_id)?.type === 'tunantada' || categories.find(c => c.id === product.category_id)?.type === 'eventos') ? (
                            <><Clock size={18} /> Ver Fechas</>
                          ) : (
                            <><ShoppingBag size={18} /> {product.stock === 0 && product.days_to_make === 0 ? 'Agotado' : 'Añadir al Carrito'}</>
                          )}
                        </button>
                      </div>

                      {/* Mobile Hover Overlay (Always visible add to cart at bottom of image) */}
                      <div className="absolute bottom-2 right-2 md:hidden z-20">
                        <button
                          onClick={(e) => {
                            const isEvent = categories.find(c => c.id === product.category_id)?.type === 'tunantada' || categories.find(c => c.id === product.category_id)?.type === 'eventos';
                            if (isEvent) {
                              e.stopPropagation();
                              window.location.hash = '#singer';
                            } else {
                              handleAddToCart(product, e);
                            }
                          }}
                          disabled={product.stock === 0 && product.days_to_make === 0}
                          className={`w-8 h-8 flex items-center justify-center rounded-full shadow-lg active:scale-95 transition-transform ${product.stock === 0 && product.days_to_make === 0 ? 'bg-mabelita-surface text-mabelita-dark/50 cursor-not-allowed' : 'bg-mabelita-primary text-mabelita-light hover:bg-mabelita-secondary'}`}
                        >
                          {(categories.find(c => c.id === product.category_id)?.type === 'tunantada' || categories.find(c => c.id === product.category_id)?.type === 'eventos') ? (
                            <Clock size={14} />
                          ) : (
                            <ShoppingBag size={14} />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col flex-1 px-1 md:px-2">
                      <div className="flex justify-between items-start gap-2 md:gap-4 mb-1 md:mb-2">
                        <div className="flex flex-col gap-1 items-start pr-1 md:pr-4">
                          {!isEvent && product.days_to_make > 0 && (
                            <span className="text-[10px] md:text-xs font-bold text-mabelita-accent uppercase tracking-widest">Por Encargo ({product.days_to_make}d)</span>
                          )}
                          {isEvent && (
                            <span className="text-[10px] md:text-xs font-bold text-emerald-600 uppercase tracking-widest">🎤 Contratación</span>
                          )}
                          <h3 className="font-serif text-sm md:text-xl lg:text-2xl font-bold text-mabelita-dark leading-tight transition-colors line-clamp-2">
                            {product.name}
                          </h3>
                        </div>
                        <span className="font-mono font-bold text-mabelita-dark bg-mabelita-light/50 px-2 py-1 md:px-3 md:py-1.5 rounded-lg md:rounded-xl text-sm md:text-lg shrink-0">
                          €{product.price.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs md:text-base text-mabelita-dark/70 line-clamp-2 mt-0.5 md:mt-1 font-light leading-snug md:leading-relaxed">{product.description}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* PRODUCT MODAL PRO */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-6 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-mabelita-dark/60 backdrop-blur-md" onClick={() => setSelectedProduct(null)}></div>

          <div className="relative bg-mabelita-light w-full md:max-w-5xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row h-[90vh] md:h-auto max-h-[90vh] animate-in slide-in-from-bottom-full md:slide-in-from-bottom-10 duration-500 ease-out mt-16 md:mt-0">
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 z-[210] bg-white hover:bg-gray-100 text-mabelita-dark p-2 rounded-full shadow-2xl border border-gray-200 transition-transform hover:scale-110"
            >
              <X size={20} />
            </button>

            <div className="w-full md:w-1/2 h-[45vh] md:h-[600px] relative bg-mabelita-surface flex-shrink-0 group overflow-hidden">
              {selectedProduct.video_url ? (
                <iframe
                  src={selectedProduct.video_url}
                  className="w-full h-full border-none bg-black"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={selectedProduct.name}
                />
              ) : (() => {
                const allImages = selectedProduct ? [selectedProduct.image_url, ...(selectedProduct.imagenes_adicionales || [])].filter(Boolean) : [];
                return (
                  <>
                    <img
                      key={containerIndex}
                      src={allImages[containerIndex]}
                      alt={selectedProduct?.name}
                      className="w-full h-full object-cover transition-transform duration-700 animate-in fade-in"
                    />

                    {/* Zoom Icon */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenLightbox(containerIndex); }}
                      className="absolute top-4 left-4 z-20 bg-mabelita-light/80 backdrop-blur-sm p-3 text-mabelita-dark rounded-full shadow-lg hover:bg-mabelita-light transition-all hover:scale-110 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                      title="Ampliar Imagen"
                    >
                      <Maximize2 size={24} />
                    </button>

                    {/* Navigation Arrows */}
                    {allImages.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setContainerIndex(prev => prev > 0 ? prev - 1 : allImages.length - 1); }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-mabelita-light/60 backdrop-blur-md p-3 rounded-full text-mabelita-dark hover:bg-mabelita-light shadow-lg transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:scale-110"
                        >
                          <ChevronLeft size={28} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setContainerIndex(prev => prev < allImages.length - 1 ? prev + 1 : 0); }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-mabelita-light/60 backdrop-blur-md p-3 rounded-full text-mabelita-dark hover:bg-mabelita-light shadow-lg transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:scale-110"
                        >
                          <ChevronRight size={28} />
                        </button>

                        {/* Thumbnails indicator at bottom */}
                        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2.5 z-20 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                          {allImages.map((_: any, i: number) => (
                            <button
                              key={i}
                              onClick={(e) => { e.stopPropagation(); setContainerIndex(i); }}
                              className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 ${i === containerIndex ? 'bg-mabelita-light scale-125 shadow-[0_0_8px_rgba(0,0,0,0.6)]' : 'bg-mabelita-light/50 hover:bg-mabelita-light/80'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}

              {selectedProduct?.is_featured && (
                <div className="absolute top-4 md:bottom-auto bottom-6 md:auto left-20 z-10 bg-mabelita-light/90 backdrop-blur-md px-3 py-1.5 md:px-4 md:py-2 rounded-full shadow-lg flex items-center gap-1.5 md:gap-2 font-bold text-[10px] md:text-sm text-mabelita-dark transition-opacity group-hover:opacity-0 md:group-hover:opacity-100">
                  <Star size={14} className="text-mabelita-accent fill-mabelita-accent" /> <span className="hidden md:inline">Producto</span> Destacado
                </div>
              )}
            </div>

            <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col bg-mabelita-light overflow-y-auto custom-scrollbar">
              <div className="mb-8">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-mabelita-primary mb-3">
                  <span>{categories.find(c => c.id === selectedProduct.category_id)?.name || 'Colección'}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-mabelita-dark mb-4 leading-tight">
                  {selectedProduct.name}
                </h2>
                <p className="text-4xl font-light text-mabelita-dark mb-6 font-mono">
                  €{(selectedProduct.price || 0).toFixed(2)}
                </p>
                <div className="h-px w-full bg-mabelita-surface mb-6"></div>
                {selectedProduct.descripcion_html ? (
                  <div className="prose prose-sm md:prose-base max-w-none text-mabelita-dark/80 font-light" dangerouslySetInnerHTML={{ __html: selectedProduct.descripcion_html }} />
                ) : (
                  <p className="text-mabelita-dark/80 text-base md:text-lg leading-relaxed font-light">
                    {selectedProduct.description}
                  </p>
                )}
              </div>

              {(() => {
                const isEventModal = categories.find(c => c.id === selectedProduct.category_id)?.type === 'tunantada' || categories.find(c => c.id === selectedProduct.category_id)?.type === 'eventos';
                if (isEventModal) {
                  return (
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <div className="flex flex-col gap-1 p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                        <Clock className="opacity-50 mb-2 text-emerald-600" size={24} />
                        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Duración</p>
                        <p className="font-medium text-mabelita-dark">2 horas por bloque</p>
                      </div>
                      <div className="flex flex-col gap-1 p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                        <Star className="opacity-50 mb-2 text-emerald-600" size={24} />
                        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Formato</p>
                        <p className="font-medium text-mabelita-dark">Show en vivo</p>
                      </div>
                      <div className="flex flex-col gap-1 p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                        <Package className="opacity-50 mb-2 text-emerald-600" size={24} />
                        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Modalidad</p>
                        <p className="font-medium text-mabelita-dark">Presencial / Videollamada</p>
                      </div>
                      <div className="flex flex-col gap-1 p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                        <Leaf className="opacity-50 mb-2 text-emerald-600" size={24} />
                        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Reserva</p>
                        <p className="font-medium text-mabelita-dark">Selecciona tu fecha</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="flex flex-col gap-1 p-4 bg-mabelita-surface/50 rounded-2xl border border-mabelita-surface">
                      <Clock className="opacity-50 mb-2" size={24} />
                      <p className="text-xs font-bold uppercase tracking-wider opacity-70">Preparación</p>
                      <p className="font-medium text-mabelita-dark">{selectedProduct.days_to_make > 0 ? `${selectedProduct.days_to_make} d. laborables` : 'Envío rápido 24h'}</p>
                    </div>
                    <div className="flex flex-col gap-1 p-4 bg-mabelita-surface/50 rounded-2xl border border-mabelita-surface">
                      <Truck className="opacity-50 mb-2" size={24} />
                      <p className="text-xs font-bold uppercase tracking-wider opacity-70">Envío</p>
                      <p className="font-medium text-mabelita-dark">Nac/Int: €{selectedProduct.shipping_cost_national || 0} / €{selectedProduct.shipping_cost_international || 0}</p>
                    </div>
                    <div className="flex flex-col gap-1 p-4 bg-mabelita-surface/50 rounded-2xl border border-mabelita-surface">
                      <Package className="opacity-50 mb-2" size={24} />
                      <p className="text-xs font-bold uppercase tracking-wider opacity-70">Stock</p>
                      <p className="font-medium text-mabelita-dark">{selectedProduct.stock > 0 ? `${selectedProduct.stock} uds.` : (selectedProduct.days_to_make > 0 ? 'Bajo pedido' : 'Agotado')}</p>
                    </div>
                    <div className="flex flex-col gap-1 p-4 bg-mabelita-surface/50 rounded-2xl border border-mabelita-surface">
                      <Leaf className="opacity-50 mb-2" size={24} />
                      <p className="text-xs font-bold uppercase tracking-wider opacity-70">Calidad</p>
                      <p className="font-medium text-mabelita-dark">Garantizada</p>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-auto pt-6 bg-mabelita-light sticky bottom-0 border-t border-mabelita-surface z-50">
                {selectedProduct.stock === 0 && selectedProduct.days_to_make === 0 ? (
                  <Button
                    size="lg"
                    disabled
                    className="w-full text-lg py-5 shadow-none bg-mabelita-surface text-mabelita-dark/50 rounded-2xl flex justify-center items-center gap-3 cursor-not-allowed"
                  >
                    Producto Agotado
                  </Button>
                ) : (categories.find(c => c.id === selectedProduct.category_id)?.type === 'eventos' || categories.find(c => c.id === selectedProduct.category_id)?.type === 'tunantada') ? (
                  <Button
                    size="lg"
                    className="w-full text-lg py-5 shadow-xl hover:-translate-y-1 transition-all duration-300 bg-mabelita-primary hover:bg-mabelita-secondary text-mabelita-light rounded-2xl flex justify-center items-center gap-3"
                    onClick={() => { setSelectedProduct(null); window.location.hash = '#singer'; }}
                  >
                    Ver Fechas Disponibles <Clock size={20} />
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="w-full text-lg py-5 shadow-xl hover:-translate-y-1 transition-all duration-300 bg-mabelita-primary hover:bg-mabelita-secondary text-mabelita-light rounded-2xl flex justify-center items-center gap-3"
                    onClick={() => handleAddToCart(selectedProduct)}
                  >
                    Añadir a la Bolsa <ShoppingBag size={20} />
                  </Button>
                )}
                {selectedProduct.days_to_make > 0 && (
                  <p className="text-center text-xs text-mabelita-accent font-bold mt-3">⚠️ Este producto se confecciona bajo pedido y tarda aprox. {selectedProduct.days_to_make} días.</p>
                )}
                <p className="text-center text-xs opacity-50 mt-2 flex items-center justify-center gap-1"><Eye size={12} /> Pago 100% seguro. Pedido protegido por Stripe.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCT LIGHTBOX GALLERY */}
      {lightboxOpen && selectedProduct && (() => {
        const allImages = [selectedProduct.image_url, ...(selectedProduct.imagenes_adicionales || [])].filter(Boolean);
        if (allImages.length === 0) return null;

        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-0 animate-in fade-in duration-300">
            <div className="absolute inset-0 bg-mabelita-dark/95 backdrop-blur-xl" onClick={() => setLightboxOpen(false)}></div>

            {/* Controles top */}
            <div className="absolute top-4 right-4 md:top-8 md:right-8 z-[310] flex gap-4">
              <button
                onClick={() => setLightboxOpen(false)}
                className="text-mabelita-light/70 hover:text-mabelita-light bg-black/20 p-3 rounded-full hover:bg-black/40 transition-colors"
                title="Cerrar (Esc)"
              >
                <X size={28} />
              </button>
            </div>

            {/* Navegación lateral */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev > 0 ? prev - 1 : allImages.length - 1)); }}
                  className="absolute left-2 md:left-8 z-[310] text-mabelita-light/50 p-3 md:p-6 hover:text-mabelita-light hover:bg-mabelita-light/10 rounded-full transition-all active:scale-95"
                >
                  <ChevronLeft size={36} className="md:w-12 md:h-12" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev < allImages.length - 1 ? prev + 1 : 0)); }}
                  className="absolute right-2 md:right-8 z-[310] text-mabelita-light/50 p-3 md:p-6 hover:text-mabelita-light hover:bg-mabelita-light/10 rounded-full transition-all active:scale-95"
                >
                  <ChevronRight size={36} className="md:w-12 md:h-12" />
                </button>
              </>
            )}

            {/* Imagen central con zoom interactivo */}
            <div className="relative z-[205] w-full h-full max-h-[100vh] flex flex-col items-center justify-center p-4 md:p-16 overflow-hidden">
              <img
                key={lightboxIndex} /* Force re-render on sliding */
                src={allImages[lightboxIndex]}
                className="max-w-full max-h-full object-contain drop-shadow-2xl animate-in fade-in zoom-in-95 duration-300"
                alt={`Vista ${lightboxIndex + 1}`}
              />
            </div>

            {/* Carrusel de miniaturas (Thumb কাশী) */}
            {allImages.length > 1 && (
              <div className="absolute bottom-6 md:bottom-12 left-1/2 transform -translate-x-1/2 z-[310] flex gap-2 md:gap-3 overflow-x-auto max-w-[90vw] p-2 bg-black/30 rounded-2xl backdrop-blur-md custom-scrollbar">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                    className={`shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 ease-out ${i === lightboxIndex ? 'border-mabelita-light scale-105 shadow-[0_0_20px_rgba(255,255,255,0.3)] opacity-100 z-10' : 'border-transparent opacity-40 hover:opacity-100 hover:scale-100'}`}
                  >
                    <img src={img} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })()}
    </div>
  );
};
